import { useCallback, useEffect, useRef, useState } from 'react'

import { getPuzzelApi } from '../../puzzelApi'
import type { WidgetState } from '../Copilot'
import type {
  SYSTEM_CALLOUT_CALL,
  SYSTEM_CALL_ASR_TRANSCRIPTION,
  SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER,
  SYSTEM_CALL_TRIGGER,
  SYSTEM_INCOMING_CALL,
} from '../eventTypes'
import { logEvents } from '../logEvents'
import { clearSessionState, loadSessionState } from '../sessionPersistence'
import { setSessionUid, transcriptionStore } from '../sessionStore'
import { clearFeed, hydrateFeed } from '../useFeed'

export type EventState = {
  currentCallState: WidgetState['currentCallState']
  data: WidgetState['data']
}

type CallState = {
  sessionId: string
  isEnded: boolean
}

const MAX_PERSIST_AGE_MS = 12 * 60 * 60 * 1000 // 12 hours

// Documentation:
// https://help.puzzel.com/api-documentation/widget-api/widget-events

export const usePuzzelEvents = (): EventState => {
  const currentSessionIdRef = useRef<string>('')

  const updateCurrentSession = useCallback((sessionId: string) => {
    currentSessionIdRef.current = sessionId
    setSessionUid(sessionId)
  }, [])

  const getTranscription = useCallback(() => transcriptionStore.state, [])

  const [widgetState, setWidgetState] = useState<EventState>({
    currentCallState: 'awaiting',
    data: {
      customer: '',
      sessionUid: '',
      queue: '',
      hasTranscription: false,
      getTranscription,
    },
  })

  useEffect(() => {
    let unsubs: (() => void)[] = []
    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    logEvents({
      event: 'USE_PUZZEL_EVENTS_MOUNT',
      currentSessionId: currentSessionIdRef.current,
    })

    const doStuff = async (): Promise<void> => {
      if (cancelled) return

      const CONNECTION_TIMEOUT_MS = 30_000
      let connected = false
      timeoutId = setTimeout(() => {
        if (connected) return
        console.warn(
          `[puzzel-widget] Connection taking longer than ${CONNECTION_TIMEOUT_MS}ms - possible PCC handshake issue`,
        )
        logEvents({
          event: 'WIDGET_CONNECT_TIMEOUT',
          timeoutMs: CONNECTION_TIMEOUT_MS,
        })
      }, CONNECTION_TIMEOUT_MS)

      try {
        const api = await getPuzzelApi()
        connected = true
        if (timeoutId) clearTimeout(timeoutId)

        // Fire-and-forget read — saves continue without the username if
        // this never resolves. Spread on `c.data` preserves any state
        // rebuild (incoming call, persisted-restore) that ran in between.
        api
          .get('auth.userName')
          .then((authUserName: unknown) => {
            if (
              cancelled ||
              typeof authUserName !== 'string' ||
              !authUserName
            ) {
              return
            }
            setWidgetState((c) => ({
              ...c,
              data: { ...c.data, agentUserName: authUserName },
            }))
          })
          .catch((err: unknown) => {
            logEvents({
              event: 'AUTH_USER_NAME_GET_FAILED',
              message: err instanceof Error ? err.message : String(err),
            })
          })

        logEvents({
          event: 'WIDGET_CONNECT_SUCCESS',
        })

        let hasTranscription = false
        let prevSessionId = ''

        unsubs.push(
          // oxlint-disable-next-line typescript/no-explicit-any -- no types here
          api.on('SYSTEM_REQUEST_IS_CONNECTED', (data: any) => {
            logEvents({ event: 'SYSTEM_REQUEST_IS_CONNECTED', data })
          }),
        )
        unsubs.push(
          api.on('SYSTEM_INCOMING_CALL', async (data: SYSTEM_INCOMING_CALL) => {
            transcriptionStore.setState(() => '')
            hasTranscription = false
            clearFeed()

            setWidgetState((c) => ({
              ...c,
              currentCallState: 'handling',
              data: {
                queue: data.call.queueKey,
                sessionUid: data.call.sessionId,
                customer: data.call.caller,
                agentUserName: c.data.agentUserName,
                hasTranscription,
                getTranscription,
              },
            }))

            updateCurrentSession(data.call.sessionId)

            logEvents({
              event: 'SYSTEM_INCOMING_CALL',
              prevSessionId,
              data,
            })
            prevSessionId = data.call.sessionId

            // Ensure we pick up the freshest state if we attached late
            try {
              const latestCall = await api.call(
                'call.getState',
                data.call.sessionId,
              )
              const sessionMatches =
                latestCall?.sessionId === currentSessionIdRef.current

              if (sessionMatches) {
                setWidgetState((c) => ({
                  ...c,
                  currentCallState: latestCall.isConnected
                    ? 'handling'
                    : c.currentCallState,
                  data: {
                    ...c.data,
                    sessionUid: latestCall.sessionId || c.data.sessionUid,
                    customer: latestCall.caller || c.data.customer,
                    queue: latestCall.queueKey || c.data.queue,
                  },
                }))
              }

              logEvents({
                event: 'CALL_GET_STATE_RESULT',
                sessionMatches,
                currentSessionId: currentSessionIdRef.current,
                latestCall,
              })
            } catch (err) {
              logEvents({
                event: 'CALL_GET_STATE_FAILED',
                message: err instanceof Error ? err.message : String(err),
              })
            }
          }),
        )

        unsubs.push(
          api.on('SYSTEM_CALLOUT_CALL', (data: SYSTEM_CALLOUT_CALL) => {
            transcriptionStore.setState(() => '')
            hasTranscription = false
            clearFeed()

            setWidgetState((c) => ({
              ...c,
              currentCallState: 'alerting',
              data: {
                queue: data.call.queueKey,
                sessionUid: data.call.sessionId,
                customer: data.call.caller,
                agentUserName: c.data.agentUserName,
                hasTranscription: false,
                getTranscription,
              },
            }))

            updateCurrentSession(data.call.sessionId)
            prevSessionId = data.call.sessionId

            logEvents({
              event: 'SYSTEM_CALLOUT_CALL',
              data,
            })
          }),
        )

        unsubs.push(
          // oxlint-disable-next-line typescript/no-explicit-any
          api.on('AGENT_TO_AGENT_CALL', (data: any) => {
            logEvents({
              event: 'AGENT_TO_AGENT_CALL',
              data,
            })
          }),
        )

        unsubs.push(
          api.on('SYSTEM_CALL_TRIGGER', (vars: SYSTEM_CALL_TRIGGER) => {
            switch (vars.system_call_progress) {
              //   case 'ALERTING':
              //     setWidgetState((c) => ({ ...c, currentCallState: 'alerting' }))
              //     break
              case 'CONNECTED': {
                setWidgetState((c) => ({ ...c, currentCallState: 'handling' }))
                break
              }
              case 'HANGUP': {
                setWidgetState((c) => ({ ...c, currentCallState: 'wrapUp' }))
                break
              }

              default: {
                break
              }
            }

            logEvents({ event: 'SYSTEM_CALL_TRIGGER', data: vars })
          }),
        )

        unsubs.push(
          api.on(
            'SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER',
            (data: SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER): void => {
              transcriptionStore.setState(
                (t) => `${t}\n${formatRole(data.role)}: ${data.message}`,
              )
              const totalWordCount = transcriptionStore.state.split(' ').length

              if (hasTranscription === false && totalWordCount > 50) {
                hasTranscription = true
                setWidgetState((c) => ({
                  ...c,
                  data: { ...c.data, hasTranscription: true },
                }))
              }

              logEvents({
                event: 'SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER',
                data: {
                  hasTranscription,
                  role: data.role,
                  wordCount: countWords(data.message),
                  totalWordCount,
                },
              })
            },
          ),
        )

        unsubs.push(
          api.on(
            'SYSTEM_CALL_ASR_TRANSCRIPTION',
            (data: SYSTEM_CALL_ASR_TRANSCRIPTION): void => {
              const vars = data?.call?.vars
              const message = vars?.message

              logEvents({
                event: 'SYSTEM_CALL_ASR_TRANSCRIPTION',
                receivedMessage: typeof message === 'string',
                role: vars?.role,
                wordCount: countWords(message),
              })
            },
          ),
        )

        await api.ready()

        let currentCall: CallState | null = null
        try {
          currentCall = await api.call('call.getCurrentCall')
        } catch (err) {
          logEvents({
            event: 'CALL_GET_STATE_FAILED',
            message: err instanceof Error ? err.message : String(err),
          })
        }

        const activeSessionId =
          currentCall?.sessionId && currentCall?.isEnded !== true
            ? currentCall?.sessionId
            : null

        if (activeSessionId) {
          // PCC does not replay events after a widget is revived/offloaded.
          // Attach to the active call explicitly so we don't stay in "awaiting".
          clearFeed()
          transcriptionStore.setState(() => '')
          updateCurrentSession(activeSessionId)

          try {
            const latestCall = await api.call('call.getState', activeSessionId)
            setWidgetState((c) => ({
              ...c,
              currentCallState:
                latestCall?.isEnded === true ? 'wrapUp' : 'handling',
              data: {
                queue: latestCall?.queueKey || c.data.queue,
                sessionUid: latestCall?.sessionId || activeSessionId,
                customer: latestCall?.caller || c.data.customer,
                agentUserName: c.data.agentUserName,
                hasTranscription,
                getTranscription,
              },
            }))

            logEvents({
              event: 'CALL_ATTACH_ACTIVE',
              activeSessionId,
              latestCall,
            })
          } catch (err) {
            setWidgetState((c) => ({
              ...c,
              currentCallState: 'handling',
              data: {
                ...c.data,
                sessionUid: activeSessionId,
              },
            }))

            logEvents({
              event: 'CALL_ATTACH_ACTIVE_FAILED',
              activeSessionId,
              message: err instanceof Error ? err.message : String(err),
            })
          }
        }

        const persisted = await loadSessionState()

        if (persisted) {
          const ageMs = Date.now() - persisted.savedAt
          const isFresh = ageMs >= 0 && ageMs < MAX_PERSIST_AGE_MS

          if (!isFresh) {
            logEvents({
              event: 'SESSION_PERSISTENCE_STALE',
              ageMs,
              currentSessionId: currentSessionIdRef.current,
              persistedSessionId: persisted.session.uid,
            })
            clearSessionState()
          } else if (activeSessionId) {
            if (persisted.session.uid === activeSessionId) {
              // Auto-restore because it's proven same call
              updateCurrentSession(persisted.session.uid)
              transcriptionStore.setState(() => persisted.session.transcription)
              hasTranscription = persisted.session.hasTranscription

              setWidgetState((c) => ({
                currentCallState: persisted.session.callState,
                data: {
                  queue: persisted.session.queue,
                  sessionUid: persisted.session.uid,
                  customer: persisted.session.customer,
                  agentUserName: c.data.agentUserName,
                  hasTranscription: persisted.session.hasTranscription,
                  getTranscription,
                },
              }))

              hydrateFeed(persisted.session.uid, persisted.feed)

              logEvents({
                event: 'SESSION_PERSISTENCE_LOADED',
                sessionUid: persisted.session.uid,
                feedLength: persisted.feed.length,
                savedAt: persisted.savedAt,
              })
            } else {
              // Fresh but mismatch: user moved on / different call
              logEvents({
                event: 'SESSION_PERSISTENCE_MISMATCH',
                activeSessionId,
                persistedSessionId: persisted.session.uid,
              })
              clearSessionState()
            }
          } else {
            // No active call context; persisted is fresh - just clear storage
            logEvents({
              event: 'SESSION_PERSISTENCE_NO_ACTIVE_CALL',
              persistedSessionId: persisted.session.uid,
            })
            clearSessionState()
          }
        }
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId)
        const err = error instanceof Error ? error : new Error(String(error))
        console.error(
          '[puzzel-widget] Failed to initialize PCC connection:',
          err,
        )
        logEvents({
          event: 'WIDGET_CONNECT_FAILED',
          message: err.message,
          stack: err.stack,
        })
      }
    }

    doStuff()

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)

      unsubs.forEach((unsub) => {
        try {
          unsub()
        } catch {
          // do nothing
        }
      })
      unsubs = []
    }
  }, [getTranscription, updateCurrentSession])

  return widgetState
}

const formatRole = (role: string): string => {
  if (role === 'Caller') return 'Customer'
  if (role === 'Callee') return 'Agent'
  return role
}

const countWords = (text: unknown): number =>
  typeof text === 'string' ? (text.match(/\S+/gu)?.length ?? 0) : 0
